import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { surveyPlansApi } from '../surveyApi';
import { PlanEmployeePicker, type PlanEmployee } from './PlanEmployeePicker';

const ID = 'survey-plan-personnel';

/**
 * Adds personnel to an existing development plan (product call, 13 Oct 2026) — the Personnel
 * tab's Add. It offers what the plan's Create form offers: ANY employee, searched and added to a
 * table — assessed or not, and whether or not they answered the survey. Each one added gets their
 * own copy of the plan as it was created — all or none; the server refuses one who already has a
 * plan in this survey, naming their row. Writing takes `surveys.plan`.
 *
 * Autogenerate adds the first three employees the search has loaded: the only thing this form
 * asks is who.
 */
export function SurveyPlanAddPersonnelModal({
  open,
  surveyId,
  planId,
  planTitle,
  onClose,
  onAdded,
}: {
  open: boolean;
  surveyId: string;
  planId: string;
  planTitle: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const toast = useToast();

  const [people, setPeople] = useState<PlanEmployee[]>([]);
  const [offered, setOffered] = useState<PlanEmployee[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = people.length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await surveyPlansApi.addMembers(surveyId, planId, { respondentIds: [], personnelIds: people.map((e) => e.id) });

      toast.success(`Added ${people.length} ${people.length === 1 ? 'employee' : 'employees'} to "${planTitle}".`);
      onAdded();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('The personnel could not be added.', 0);

      setError(failure);

      // One picker stands for the whole list, so a refusal naming a row lands on it.
      const fieldIds: Record<string, string> = { PersonnelIds: `${ID}-personnel-ids`, personnelIds: `${ID}-personnel-ids` };

      people.forEach((_, i) => {
        fieldIds[`PersonnelIds[${i}]`] = `${ID}-personnel-ids`;
        fieldIds[`personnelIds[${i}]`] = `${ID}-personnel-ids`;
      });

      if (failure.fields && Object.keys(failure.fields).length > 0) focusValidationError(failure, { idPrefix: ID, fieldIds });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add personnel"
      size="xl"
      id={`${ID}-modal`}
      autogenerate={{ onGenerate: () => setPeople(offered.slice(0, 3)), disabled: saving || offered.length === 0 }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* The consequence, before the tick: adding somebody writes their plan. */}
        <p className="text-xs text-slate-600">
          Each employee added gets their own copy of this development plan as it was created, to be submitted for
          approval on its own.
        </p>

        <PlanEmployeePicker idPrefix={ID} value={people} onChange={setPeople} onOffered={setOffered} disabled={saving} />
      </div>
    </Modal>
  );
}
